/**
 * User Invitation model interfaces matching the User Invitation API specification
 * 
 * @remarks
 * These models correspond to the User Invitation API endpoints at `/api/invitations`
 * All interfaces are designed to match the API response structure exactly.
 * 
 * **Invitation Flow:**
 * 1. Admin sends invitations → Emails sent with token
 * 2. User accepts invitation → Marks invitation as accepted
 * 3. User registers/links account → Links user to organization
 * 
 * @see USER_INVITATION_API_REFERENCE.md for complete API documentation
 */

import { BaseEntity, PaginationMeta } from './base';

/**
 * Organization reference in UserInvitation
 * 
 * @remarks
 * This represents the populated organization data in API responses.
 * The backend stores this as an ObjectId reference but populates it in responses.
 */
export interface InvitationOrganization {
  /**
   * Organization MongoDB ObjectId
   */
  _id: string;

  /**
   * Organization name
   */
  name: string;

  /**
   * Organization code (unique identifier)
   */
  orgCode: string;
}

/**
 * Core UserInvitation model matching API schema
 * 
 * @remarks
 * Invitations have a 7-day expiration period.
 * TTL index automatically deletes expired invitations from the database.
 * 
 * **Schema Change (v2.0):**
 * - Backend now stores `organization` as ObjectId reference (not plain orgCode string)
 * - API responses include populated organization object: { _id, name, orgCode }
 * - For backward compatibility, you can access orgCode via invitation.organization.orgCode
 */
export interface UserInvitation extends BaseEntity {
  /**
   * Email address of the invited user (indexed)
   */
  email: string;

  /**
   * Name of the invited user
   */
  name: string;

  /**
   * Organization reference (populated in API responses)
   * 
   * @remarks
   * Backend stores this as ObjectId reference to Organization collection.
   * API automatically populates this with { _id, name, orgCode }
   * 
   * **Migration Note:** Previously this was `orgCode: string`
   * Now access via: `invitation.organization.orgCode`
   */
  organization: InvitationOrganization;

  /**
   * Role the user will have in the organization
   * Typically: 'Admin', 'Editor', or 'Reader'
   */
  role: string;

  /**
   * Unique invitation token (16-character hex string, indexed)
   * Used in the invitation accept URL
   */
  token: string;

  /**
   * Whether the invitation has been accepted
   * @default false
   */
  accepted: boolean;

  /**
   * Whether the user has registered/linked their account
   * @default false
   */
  registered: boolean;

  /**
   * When the invitation expires (7 days from creation)
   * Expired invitations are automatically deleted by TTL index
   */
  expiresAt: string;

  /**
   * When the invitation was accepted
   * Only present if accepted is true
   */
  acceptedAt?: string;

  /**
   * When the user registered/linked their account
   * Only present if registered is true
   */
  registeredAt?: string;

  /**
   * Whether the invited email already has an account in the application
   * at the time the invitation was created.
   *
   * - `'N'` — **New** user: no existing account found for this email
   * - `'E'` — **Existing** user: an account already exists for this email
   *
   * @remarks
   * This is a **server-computed, point-in-time snapshot** — it reflects the
   * state at invite-creation time, not the current state.
   * Field is optional to maintain backward compatibility with older invitation
   * documents created before this field was introduced.
   * Handle gracefully in templates: `invitation.userType ?? '—'`
   */
  userType?: 'N' | 'E';
}

/**
 * Data Transfer Object for creating a single invitation
 * 
 * @remarks
 * Used as array item in POST /api/invitations
 * Send invitations endpoint accepts an array of these objects.
 * 
 * **Note:** Request uses orgCode (string), but backend converts it to organization ObjectId reference.
 * Response will include populated organization object.
 */
export interface CreateInvitationDto {
  /**
   * Email address of the user to invite (required, valid email)
   */
  email: string;

  /**
   * Name of the user to invite (required)
   */
  name: string;

  /**
   * Organization code to invite the user to (required)
   * Organization must exist and sender must be Admin
   * 
   * @remarks
   * Backend will lookup organization by orgCode and store ObjectId reference.
   * API response will include populated organization data.
   */
  orgCode: string;

  /**
   * Role to assign to the user (required)
   * Typically: 'Admin', 'Editor', or 'Reader'
   */
  role: string;
}

/**
 * Data Transfer Object for accepting an invitation
 * 
 * @remarks
 * Used in PUT /api/invitations/:token/accept (Public endpoint)
 * Marks the invitation as accepted.
 */
export interface AcceptInvitationDto {
  /**
   * Optional: User's name if they want to update it
   */
  name?: string;
}

/**
 * Data Transfer Object for validating an invitation token
 * 
 * @remarks
 * Used in GET /api/invitations/:token (Public endpoint)
 * Returns invitation details if token is valid and not expired.
 */
export interface ValidateInvitationResponse {
  /**
   * Whether the token is valid
   */
  valid: boolean;

  /**
   * Invitation details if valid
   */
  invitation?: UserInvitation;

  /**
   * Error message if invalid
   */
  message?: string;
}

/**
 * API response wrapper for sending invitations
 * 
 * @remarks
 * Returns array of created invitations
 */
export interface SendInvitationsResponse {
  /**
   * Array of created invitations
   */
  invitations: UserInvitation[];

  /**
   * Optional success message
   */
  message?: string;
}

/**
 * Filter parameters for querying invitations
 * 
 * @remarks
 * All parameters are optional except orgId.
 * Supports filtering by status, expiration, and text search.
 */
export interface InvitationFilters {
  /**
   * Organization ID (MongoDB ObjectId) - REQUIRED
   * User must have access to this organization
   */
  orgId: string;

  /**
   * Page number for pagination (default: 1)
   */
  page?: number;

  /**
   * Items per page (default: 10, max: 100)
   */
  limit?: number;

  /**
   * Filter by expiration status
   * - true: only expired invitations
   * - false: only non-expired invitations
   */
  expired?: boolean;

  /**
   * Filter by acceptance status
   * - true: only accepted invitations
   * - false: only unaccepted invitations
   */
  accepted?: boolean;

  /**
   * Filter by registration status
   * - true: only registered invitations
   * - false: only unregistered invitations
   */
  registered?: boolean;

  /**
   * Search by user name (case-insensitive, partial match)
   */
  userName?: string;

  /**
   * Search by email (case-insensitive, partial match)
   */
  email?: string;
}

/**
 * API response wrapper for getting invitations with pagination
 * 
 * @remarks
 * New enhanced format that includes pagination metadata.
 * Supports advanced filtering and search capabilities.
 */
export interface PaginatedInvitationsResponse {
  /**
   * Response status (always 'success' on successful requests)
   */
  status: 'success';

  /**
   * Response data containing invitations and pagination info
   */
  data: {
    /**
     * Array of invitations matching the filter criteria
     */
    invitations: UserInvitation[];

    /**
     * Pagination metadata
     */
    pagination: PaginationMeta;
  };
}

/**
 * API response wrapper for getting all invitations (Legacy)
 * 
 * @remarks
 * @deprecated Use PaginatedInvitationsResponse instead for new code.
 * This interface is maintained for backward compatibility.
 * Returns only non-expired and unaccepted invitations.
 */
export interface GetInvitationsResponse {
  /**
   * Array of pending invitations
   */
  invitations: UserInvitation[];
}

/**
 * API response wrapper for getting invitation by token
 */
export interface GetInvitationByTokenResponse {
  /**
   * Invitation details
   */
  invitation: UserInvitation;
}

/**
 * API response wrapper for accepting an invitation
 */
export interface AcceptInvitationResponse {
  /**
   * Success message
   */
  message: 'Invitation accepted successfully';

  /**
   * Updated invitation
   */
  invitation: UserInvitation;
}

/**
 * API response wrapper for deleting an invitation
 */
export interface DeleteInvitationResponse {
  /**
   * Success message
   */
  message: 'Invitation deleted successfully';
}

/**
 * API response wrapper for marking an invitation as registered
 *
 * @remarks
 * Returned by POST /api/invitations/:token/register
 * Called after a user completes registration to mark the invitation
 * as fully onboarded.
 */
export interface MarkRegisteredResponse {
  /**
   * Success message
   */
  message: 'Invitation registered successfully';

  /**
   * Updated invitation with registered = true and registeredAt populated
   */
  invitation: UserInvitation;
}

/**
 * Organization reference in the link-user response
 *
 * @remarks
 * Note: the API returns `id` (not `_id`) in this specific response shape.
 */
export interface LinkUserOrganizationRef {
  /** Organization MongoDB ObjectId */
  id: string;
  /** Organization name */
  name: string;
  /** Organization code */
  orgCode: string;
}

/**
 * API response wrapper for linking an existing user to an organization
 *
 * @remarks
 * Returned by PUT /api/invitations/:token/link-user
 * Used when an already-registered user accepts an invitation.
 * Requires authentication; the authenticated user's email must match
 * the invitation email.
 */
export interface LinkUserResponse {
  /**
   * Success message
   */
  message: 'User linked to organization successfully';

  /**
   * Response payload
   */
  data: {
    /**
     * Updated invitation fields (partial — only changed fields are returned)
     */
    invitation: Pick<UserInvitation, '_id' | 'registered' | 'registeredAt'>;

    /**
     * Organization the user was linked to
     */
    organization: LinkUserOrganizationRef;

    /**
     * Role assigned to the user within the organization
     */
    userRole: string;
  };
}
